import { ConvexError, v, type Infer } from 'convex/values';

/**
 * How a member's IMAI looks and what it is called. The look is a squishmoji
 * blob: a shape, a face, a fill style, and a colour seed that picks the
 * palette. Every field is optional; the client fills in the defaults.
 */
export const yourImaiPersonaValidator = v.object({
  name: v.optional(v.string()),
  shape: v.optional(v.string()),
  expression: v.optional(v.string()),
  backgroundStyle: v.optional(v.string()),
  colorSeed: v.optional(v.string()),
});

export type YourImaiPersona = Infer<typeof yourImaiPersonaValidator>;

export const MAX_YOUR_IMAI_NAME_LENGTH = 32;

// The squishmoji catalog ids (kept here so Convex need not bundle the
// renderer).
export const YOUR_IMAI_SHAPE_IDS = [
  'circle',
  'pebble',
  'squircle',
  'capsule',
  'triangle',
  'hexagon',
  'cloud',
  'drop',
  'square',
  'pentagon',
  'octagon',
  'star',
  'flower',
  'cat',
  'ghost',
  'ufo',
  'bot',
  'alien',
  'lion',
  'monkey',
  'mecha',
] as const;
export const YOUR_IMAI_EXPRESSION_IDS = [
  'neutral',
  'happy',
  'sad',
  'excited',
  'angry',
  'loving',
  'surprised',
  'laughing',
  'scared',
  'suspicious',
  'confused',
  'curious',
  'proud',
  'shy',
  'bored',
  'sleepy',
  'crazy',
  'determined',
  'guilty',
  'amazed',
  'doubtful',
  'attentive',
] as const;
const SHAPES = new Set<string>(YOUR_IMAI_SHAPE_IDS);
const EXPRESSIONS = new Set<string>(YOUR_IMAI_EXPRESSION_IDS);

/** Whether `value` is one of the faces the picker offers. */
export function isYourImaiExpression(value: string): boolean {
  return EXPRESSIONS.has(value);
}

const BACKGROUND_STYLES = new Set([
  'solid',
  'taygeta',
  'celaeno',
  'alcyone',
  'maia',
  'merope',
]);
const MAX_COLOR_SEED_LENGTH = 64;

/**
 * The picker's colours by name, for IMAI's own `yourimai persona --color`.
 * Mirrors YOUR_IMAI_COLOR_SEEDS in the client's persona catalog.
 */
export const YOUR_IMAI_COLOR_SEED_BY_NAME = {
  lavender: 'imai-22',
  blue: 'imai-67',
  yellow: 'imai-190',
  magenta: 'imai-5',
  lime: 'imai-43',
  pink: 'imai-55',
  mint: 'imai-4',
  orange: 'imai-25',
  violet: 'imai-24',
  red: 'imai-50',
  cyan: 'imai-6',
  black: 'imai-15',
} as const;

export type YourImaiColorName = keyof typeof YOUR_IMAI_COLOR_SEED_BY_NAME;

export const YOUR_IMAI_COLOR_NAMES = [
  'lavender',
  'blue',
  'yellow',
  'magenta',
  'lime',
  'pink',
  'mint',
  'orange',
  'violet',
  'red',
  'cyan',
  'black',
] as const satisfies readonly YourImaiColorName[];

/** The colour name of a stored seed, or null for a seed outside the picker. */
export function yourImaiColorNameOf(
  colorSeed: string | undefined
): YourImaiColorName | null {
  return (
    YOUR_IMAI_COLOR_NAMES.find(
      (name) => YOUR_IMAI_COLOR_SEED_BY_NAME[name] === colorSeed
    ) ?? null
  );
}

/**
 * A persona as stored: trimmed, catalog ids only, empty fields dropped.
 * Throws on a value the picker could not have produced.
 */
export function normalizeYourImaiPersona(
  persona: YourImaiPersona
): YourImaiPersona {
  const name = persona.name?.trim().replace(/\s+/g, ' ');
  if (name && name.length > MAX_YOUR_IMAI_NAME_LENGTH)
    throw new ConvexError('YOUR_IMAI_NAME_TOO_LONG');
  const pick = (value: string | undefined, allowed: Set<string>) => {
    if (value === undefined) return undefined;
    if (!allowed.has(value)) throw new ConvexError('YOUR_IMAI_INVALID_LOOK');
    return value;
  };
  const colorSeed = persona.colorSeed?.trim();
  if (colorSeed && colorSeed.length > MAX_COLOR_SEED_LENGTH)
    throw new ConvexError('YOUR_IMAI_INVALID_LOOK');
  const normalized: YourImaiPersona = {
    name: name || undefined,
    shape: pick(persona.shape, SHAPES),
    expression: pick(persona.expression, EXPRESSIONS),
    backgroundStyle: pick(persona.backgroundStyle, BACKGROUND_STYLES),
    colorSeed: colorSeed || undefined,
  };
  return Object.fromEntries(
    Object.entries(normalized).filter(([, value]) => value !== undefined)
  );
}
